/**
 * EIP-6963 multi-wallet discovery with a legacy `window.ethereum` fallback.
 *
 * - Listens for `eip6963:announceProvider` and dispatches `eip6963:requestProvider`.
 * - If `window.ethereum` exists and was not announced via EIP-6963 (old wallets, some in-app browsers),
 *   it is offered as a synthetic entry with rdns `injected`, named from its is* flags (Rabby/Brave/OKX also set
 *   isMetaMask, so they are checked first; see compat.ts).
 * - Order: MetaMask, Rabby, then the other researched wallets, unknown wallets, unsupported ones last (#687).
 */
import { walletProfile } from './compat';
import { t } from '../i18n/i18n';
import type { EIP1193Provider, EIP6963ProviderDetail } from './types';

export const INJECTED_RDNS = 'injected';
export const METAMASK_RDNS = 'io.metamask';

// Generic wallet glyph for the injected fallback (no icon is available for it).
const INJECTED_ICON =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#8b94a6" stroke-width="2">' +
      '<rect x="3" y="6" width="18" height="13" rx="2"/><path d="M16 12h2"/><path d="M3 9h18"/></svg>',
  );

type WindowWithEthereum = Window & { ethereum?: EIP1193Provider };

export function isMetaMaskRdns(rdns: string): boolean {
  return rdns === METAMASK_RDNS || rdns.startsWith(`${METAMASK_RDNS}.`);
}

function isMetaMaskDetail(d: EIP6963ProviderDetail): boolean {
  if (isMetaMaskRdns(d.info.rdns)) return true;
  return d.info.rdns === INJECTED_RDNS && walletProfile(null, d.provider).id === 'metamask';
}

/** Picker rank: profile rank (MetaMask 0, Rabby 1, …, unknown 50, unsupported 90). */
export function walletRank(d: EIP6963ProviderDetail): number {
  if (isMetaMaskDetail(d)) return 0;
  return walletProfile(d.info.rdns, d.provider).rank;
}

/** By rank, otherwise announcement order (Array.prototype.sort is stable). */
export function sortWallets(list: readonly EIP6963ProviderDetail[]): EIP6963ProviderDetail[] {
  return [...list].sort((a, b) => walletRank(a) - walletRank(b));
}

function injectedDetail(provider: EIP1193Provider): EIP6963ProviderDetail {
  const profile = walletProfile(null, provider);
  return {
    info: {
      uuid: 'injected',
      name: profile.id === 'generic' ? t('wallet.generic_name') : profile.name,
      icon: INJECTED_ICON,
      rdns: INJECTED_RDNS,
    },
    provider,
  };
}

export interface WalletDiscovery {
  /** Current list (MetaMask first). Same array instance until something changes. */
  getWallets(): readonly EIP6963ProviderDetail[];
  subscribe(listener: () => void): () => void;
  /** Re-dispatch eip6963:requestProvider (e.g. when the picker opens). */
  refresh(): void;
  destroy(): void;
}

export function createWalletDiscovery(win: Window = window): WalletDiscovery {
  const announced: EIP6963ProviderDetail[] = [];
  const listeners = new Set<() => void>();
  let snapshot: readonly EIP6963ProviderDetail[] = [];

  const recompute = () => {
    const list = [...announced];
    const injected = (win as WindowWithEthereum).ethereum;
    if (injected && typeof injected.request === 'function' && !announced.some((d) => d.provider === injected)) {
      list.push(injectedDetail(injected));
    }
    const next = sortWallets(list);
    const same =
      next.length === snapshot.length &&
      next.every((d, i) => d.provider === snapshot[i]?.provider && d.info.uuid === snapshot[i]?.info.uuid);
    if (same) return;
    snapshot = next;
    listeners.forEach((l) => l());
  };

  const onAnnounce = (event: Event) => {
    const detail = (event as CustomEvent<EIP6963ProviderDetail | undefined>).detail;
    if (!detail?.info?.rdns || !detail.provider || typeof detail.provider.request !== 'function') return;
    // Wallets re-announce on every request: replace by uuid instead of duplicating.
    const idx = announced.findIndex((d) => d.info.uuid === detail.info.uuid);
    if (idx >= 0) announced[idx] = detail;
    else announced.push(detail);
    recompute();
  };

  const refresh = () => {
    win.dispatchEvent(new Event('eip6963:requestProvider'));
    recompute();
  };

  win.addEventListener('eip6963:announceProvider', onAnnounce);
  // MetaMask fires this when it injects window.ethereum late (legacy behaviour).
  win.addEventListener('ethereum#initialized', recompute);
  refresh();

  return {
    getWallets: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    refresh,
    destroy() {
      win.removeEventListener('eip6963:announceProvider', onAnnounce);
      win.removeEventListener('ethereum#initialized', recompute);
      listeners.clear();
    },
  };
}
